import { Box, useTheme } from "@mui/material";

import type { TestId } from "@storyboard/web-test-ids/testIds";

type ButtonTab = {
  key: string;
  text: string | JSX.Element;
  icon?: JSX.Element | null;
  active?: boolean;
  disabled?: boolean;
};
type ButtonTabsProps = {
  tabs: ButtonTab[];
  setActiveTab: (key: string) => void;
  /** Browser-test id stamped on every tab button; tabs are told apart by their labels. */
  testId?: TestId;
};

export default function ButtonTabs({ tabs, setActiveTab, testId }: ButtonTabsProps) {
  const jsxText = (value: string | JSX.Element) => {
    if (typeof value === "string") {
      return value;
    }
    return value?.props?.children
      ? value?.props?.children?.filter((child: any) => typeof child === "string").join(" ")
      : "";
  };
  const theme = useTheme();
  return (
    <Box
      component="ul"
      sx={{
        background: theme.palette.secondary.main,
        display: "flex",
        p: "4px",
        gap: 1,
        listStyle: "none",
        borderRadius: "0.5rem",
        m: 0,
      }}
    >
      {tabs.map((tab) => (
        <Box component="li" sx={{ flexGrow: 1 }} key={tab.key}>
          <Box
            component="button"
            data-sb={testId}
            aria-label={jsxText(tab.text)}
            onClick={() => setActiveTab(tab.key)}
            type="button"
            className="basic-button"
            sx={{
              position: "relative",
              display: "flex",
              width: "100%",
              gap: 1,
              p: 0.5,
              alignItems: "center",
              justifyContent: "center",
              background: tab.active ? theme.palette.secondary.light : "",
              "&:hover": { background: tab.active ? theme.palette.secondary.light : "transparent" },
            }}
            disabled={tab.disabled}
          >
            {tab?.icon && <span className="icon">{tab.icon}</span>}
            <span className="text">{tab.text}</span>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
